"use client";

import { motion } from "@/lib/motion";
import { Globe2 } from "@/components/icons";
import { type ProxyLocation } from "@/lib/constants";
import { msColor, cleanHost } from "@/lib/utils";

export type Loc = ProxyLocation;
export type Network = "residential" | "mobile";

export { msColor, cleanHost };

export function LocationPill({
  loc,
  selected,
  network,
  onClick,
}: {
  loc: Loc;
  selected: boolean;
  network: Network;
  onClick: () => void;
}) {
  const accent = network === "residential" ? "#4ade80" : "#60a5fa";
  return (
    <button
      onClick={onClick}
      className="relative flex w-full cursor-pointer flex-col items-center gap-0.5 rounded-xl border px-2 py-2 text-center transition-all sm:items-start sm:px-3 sm:py-2.5 sm:text-left"
      style={{
        borderColor: selected ? accent : "rgba(255,255,255,0.07)",
        background: selected
          ? `rgba(${network === "residential" ? "74,222,128" : "96,165,250"},0.08)`
          : "rgba(255,255,255,0.02)",
        boxShadow: selected ? `0 0 0 1px ${accent}40, 0 0 18px ${accent}12` : "none",
      }}
    >
      <div className="flex w-full min-w-0 flex-col items-center gap-1 sm:flex-row sm:gap-1.5">
        <span className="text-base leading-none">{loc.flag}</span>
        <span className="max-w-full truncate text-[11px] leading-tight font-semibold text-white/80 sm:text-xs sm:leading-none">
          {loc.city}
        </span>
      </div>
      <span
        className="font-mono text-[10px] leading-none sm:ml-0.5"
        style={{ color: msColor(loc.ms) }}
      >
        {loc.ms}ms
      </span>
      {selected && (
        <motion.span
          layoutId="pill-check"
          className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full"
          style={{ background: accent }}
        />
      )}
    </button>
  );
}

export function LoadingPanel({
  accent,
  selected,
  host,
  network,
}: {
  accent: string;
  selected: Loc | null;
  host: string;
  network: Network;
}) {
  return (
    <div className="px-4 py-4">
      <div className="mb-3 flex items-center justify-center gap-3">
        <div className="flex flex-col items-center gap-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] font-mono text-[10px] text-[#a0a0a0]">
            You
          </div>
        </div>

        <PulseDots accent={accent} offset={0} />

        <div className="flex flex-col items-center gap-1">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full border text-lg"
            style={{
              borderColor: `${accent}50`,
              background: `${accent}12`,
              boxShadow: `0 0 14px ${accent}25`,
            }}
          >
            {selected?.flag}
          </div>
          <span className="text-[9px] font-medium" style={{ color: accent }}>
            {selected?.city}
          </span>
        </div>

        <PulseDots accent={accent} offset={0.4} />

        <div className="flex flex-col items-center gap-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/[0.04]">
            <Globe2 size={14} className="text-white/30" aria-hidden />
          </div>
          <span className="max-w-[56px] truncate font-mono text-[9px] text-white/30">{host}</span>
        </div>
      </div>
      <p className="text-center font-mono text-[11px] text-white/25">
        Routing through {network} proxy…
      </p>
    </div>
  );
}

function PulseDots({ accent, offset }: { accent: string; offset: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          className="h-0.5 w-1.5 rounded-full"
          style={{ background: accent }}
          animate={{ opacity: [0.15, 1, 0.15] }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: offset + i * 0.12,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

export function ResponsePanel({
  loc,
  host,
  network,
  skip,
}: {
  loc: Loc;
  host: string;
  network: Network;
  skip: boolean;
}) {
  const lines = [
    { k: "HTTP/1.1", v: "200 OK", color: "#4ade80" },
    { k: "Via:", v: `proxidize/${network}`, color: "#94a3b8" },
    { k: "X-Proxy-IP:", v: loc.ip, color: "#60a5fa" },
    { k: "X-Proxy-Location:", v: `${loc.city}, ${loc.flag}`, color: "#60a5fa" },
    { k: "X-Response-Time:", v: `${loc.ms}ms`, color: msColor(loc.ms) },
    { k: "X-Request-Host:", v: host, color: "#94a3b8" },
  ];
  return (
    <div className="px-4 py-2 font-mono text-[11px] leading-snug">
      {lines.map((l, i) => (
        <motion.div
          key={l.k}
          initial={skip ? false : { opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.18, delay: i * 0.055 }}
          className="flex gap-2"
        >
          <span className="w-36 shrink-0 text-white/25">{l.k}</span>
          <span style={{ color: l.color }}>{l.v}</span>
        </motion.div>
      ))}
    </div>
  );
}

export function ResultChip({
  icon,
  label,
  color,
  mono,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  color?: string;
  mono?: boolean;
  tooltip?: string;
}) {
  return (
    <span
      className={`group/chiptip relative inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] ${tooltip ? "cursor-help" : ""}${mono ? "font-mono" : "font-medium"}`}
      style={{
        background: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.08)",
        color: color ?? "rgba(255,255,255,0.85)",
      }}
    >
      {icon}
      {label}
      {tooltip && (
        <span className="text-foreground bg-popover border-border pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 w-max -translate-x-1/2 rounded-md border px-2 py-1 text-center font-sans text-[11px] leading-snug font-normal whitespace-nowrap opacity-0 shadow-lg transition-opacity duration-150 group-hover/chiptip:opacity-100">
          {tooltip}
        </span>
      )}
    </span>
  );
}

export function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  delay,
  skip,
}: {
  icon: React.ComponentType<{
    size?: number;
    "aria-hidden"?: boolean;
    style?: React.CSSProperties;
  }>;
  label: string;
  value: string;
  sub: string;
  color: string;
  delay: number;
  skip: boolean;
}) {
  return (
    <motion.div
      initial={skip ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="rounded-xl border border-white/[0.07] bg-[#0d0d10]/80 px-4 py-3"
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon size={10} aria-hidden style={{ color }} />
        <span className="text-[10px] font-semibold tracking-widest text-white/25 uppercase">
          {label}
        </span>
      </div>
      <div className="mb-0.5 text-xl leading-none font-bold" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] text-white/20">{sub}</div>
    </motion.div>
  );
}
